import { connect } from 'react-redux';
import { compose, concat } from 'ramda';
import { withRouter } from 'react-router-dom';
import i18n from 'services/i18n';

import DeviceInformationContent from 'components/DataBlock/AdaptiveDataBlock';
import withPageLoader from 'components/withPageLoader';

import { requestNewestObservation } from 'store/actionCreators/newestObservations';
import { getPatientPageRequestParams } from 'store/selectors/router';
import {
  getObservationShouldShowLoading,
  getNewestObservationPacingSettings,
  getNewestObservationShockSettings,
  getNewestObservationBatteryLife,
  getNewestObservationReportItemShocks,
  isObservationEntryResource,
} from 'store/selectors/newestObservations';
import {
  getPatientQuestionnaireResponseDeviceImplant,
  getPatientQuestionnaireResponseDeviceImplantDate,
  getPatientQuestionnaireResponseDeviceClinicSpecial,
  getPatientQuestionnaireResponseSatelliteDeviceClinic,
  getPatientQuestionnaireResponseDeviceVendor,
  getPatientQuestionnaireResponseInactiveImplantsCount,
  getPatientQuestionnaireResponseActiveImplantsCount,
  getPatientQuestionnaireResponseHomeMonitor,
  getPatientQuestionnaireResponseSerialNumber,
  getPatientQuestionnaireResponseDeviceName,
  getPatientQuestionnaireResponseLocalDeviceClinicPhone,
  isAdvisory,
  getDeviceAdvisoryType,
  getDeviceAdvisoryLeadType,
  getDeviceAdvisoryModel,
  getDeviceAdvisorySerialNumber,
  getAlertDevice1,
  getAlertDevice1Model,
  getAlertDevice2,
  getAlertDevice2Model,
} from 'store/selectors/questionnaireResponses';

const mapStateToProps = (
  state,
  {
    match: {
      params: { id },
    },
  },
) => {
  const isReports = isObservationEntryResource(id)(state);
  const data = [
    {
      title: 'deviceInformation.typeOfDeviceImplant',
      items: [{ value: getPatientQuestionnaireResponseDeviceImplant(id)(state) }],
    },
    {
      title: 'deviceInformation.dateOfImplant',
      items: [{ value: getPatientQuestionnaireResponseDeviceImplantDate(id)(state) }],
    },
    {
      title: 'deviceInformation.deviceClinic1',
      items: [{ value: getPatientQuestionnaireResponseDeviceClinicSpecial(id)(state) }],
    },
    {
      title: 'deviceInformation.deviceClinic2',
      items: [{ value: getPatientQuestionnaireResponseSatelliteDeviceClinic(id)(state) }],
    },
    {
      title: 'deviceInformation.localDeviceClinicPhone',
      items: [{ value: getPatientQuestionnaireResponseLocalDeviceClinicPhone(id)(state) }],
    },
    {
      title: 'Baseline Device Advisory',
      items: [
        {
          value: i18n.t('deviceInformation.alertDevice1', {
            value: getAlertDevice1(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.alertDevice1Model', {
            value: getAlertDevice1Model(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.alertDevice2', {
            value: getAlertDevice2(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.alertDevice2Model', {
            value: getAlertDevice2Model(id)(state),
          }),
        },
      ],
    },
    {
      title: 'Device Advisory',
      items: [
        {
          value: i18n.t('deviceInformation.isThereAdvisory', {
            value: isAdvisory(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.advisoryType', {
            value: getDeviceAdvisoryType(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.advisoryLeadType', {
            value: getDeviceAdvisoryLeadType(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.advisoryModel', {
            value: getDeviceAdvisoryModel(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.advisorySerialNumber', {
            value: getDeviceAdvisorySerialNumber(id)(state),
          }),
        },
      ],
    },
    {
      title: 'deviceInformation.deviceDetails',
      items: [
        {
          value: i18n.t('deviceInformation.values.vendor', {
            value: getPatientQuestionnaireResponseDeviceVendor(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.values.modelName', {
            value: getPatientQuestionnaireResponseDeviceName(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.values.modelNumber', {
            value: getPatientQuestionnaireResponseSerialNumber(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.values.activeImplants', {
            value: getPatientQuestionnaireResponseActiveImplantsCount(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.values.inactiveImplants', {
            value: getPatientQuestionnaireResponseInactiveImplantsCount(id)(state),
          }),
        },
        {
          value: i18n.t('deviceInformation.values.homeMonitor', {
            value: getPatientQuestionnaireResponseHomeMonitor(id)(state),
          }),
        },
      ],
    },
  ];

  if (isReports) {
    return {
      data: concat(data, [
        {
          title: 'deviceInformation.lastUpdate',
          items: [
            {
              value: i18n.t('deviceInformation.values.batteryLife', {
                value: getNewestObservationBatteryLife(id)(state),
              }),
            },
          ],
        },
        {
          title: 'deviceInformation.lastRecordedShock',
          items: [{ value: getNewestObservationReportItemShocks(id)(state) }],
        },
        {
          title: 'deviceInformation.deviceParameters',
          items: [
            {
              value: i18n.t('deviceInformation.values.pacingSettings', {
                value: getNewestObservationPacingSettings(id)(state),
              }),
            },
            {
              value: i18n.t('deviceInformation.values.shockSettings', {
                value: getNewestObservationShockSettings(id)(state),
              }),
            },
          ],
        },
      ]),
    };
  }

  return {
    data,
  };
};

export default compose(
  withRouter,
  withPageLoader({
    loadingSelector: (
      state,
      {
        match: {
          params: { id },
        },
      },
    ) => getObservationShouldShowLoading(id)(state),
    initialAction: requestNewestObservation,
    initialActionArgsSelector: getPatientPageRequestParams,
  }),
  connect(mapStateToProps),
)(DeviceInformationContent);
